import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const PermissionContext = createContext();

export const usePermissionContext = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissionContext must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [businessId, setBusinessId] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [enabledModules, setEnabledModules] = useState([]);

  // Initial load on mount
  useEffect(() => {
    const token = localStorage.getItem('etpos_token');
    if (token) {
      loadUserPermissions();
    } else {
      setIsLoading(false);
    }

    // Listen for login events to reload permissions for new users
    const handleLoginEvent = () => {
      // console.log('🔐 RBAC DEBUG - Login event detected, clearing and reloading permissions');
      // Clear previous user's permissions
      setPermissions([]);
      setUserRole(null);
      setBusinessId(null);
      setIsSuperAdmin(false);
      // Reload for new user
      loadUserPermissions();
    };

    // Listen for settings update to reload modules & permissions
    const handleSettingsUpdate = (e) => {
      const activeTab = e?.detail?.activeTab;
      if (activeTab === 'modules' || activeTab === 'purchases') {
        // console.log(`🔐 RBAC DEBUG - Settings update for ${activeTab} detected, reloading permissions`);
        loadUserPermissions(true);
      }
    };

    window.addEventListener('user-logged-in', handleLoginEvent);
    window.addEventListener('etpos-settings-update', handleSettingsUpdate);

    return () => {
      window.removeEventListener('user-logged-in', handleLoginEvent);
      window.removeEventListener('etpos-settings-update', handleSettingsUpdate);
    };
  }, []);

  const loadUserPermissions = async (isSilent = false) => {
    // console.log('🔐 RBAC DEBUG - Loading user permissions...');
    try {
      if (!isSilent) {
        setIsLoading(true);
      }
      // console.log('🔐 RBAC DEBUG - Calling getUserDetails...');
      const userDetails = await apiService.getUserDetails();
      // console.log('🔐 RBAC DEBUG - User details received:', userDetails);

      // Extract actual user data from nested response
      const userData = userDetails.data || userDetails;
      // console.log('🔐 RBAC DEBUG - Extracted user data:', userData);

      if (userData) {
        const isImpersonating = !!localStorage.getItem('etpos_superadmin_backup_token');
        setIsSuperAdmin(!isImpersonating && (userData.is_superadmin === true || userData.is_superadmin === 1 || userData.user_type === 'superadmin'));
        if (userData.business_id) {
          setBusinessId(userData.business_id);
          // console.log('🔐 RBAC DEBUG - Business ID set:', userData.business_id);
        }

        // Store enabled_modules from business for SPA module gating (aligns with web session('business.enabled_modules'))
        const modules = userData.enabled_modules || [];
        setEnabledModules(Array.isArray(modules) ? modules : []);
        // console.log('🔐 RBAC DEBUG - Enabled modules set:', modules);

        // PRIMARY: Use all_permissions from getUserDetails() - this matches web's getAllPermissions()
        // which returns both role-based and direct permissions combined
        if (userData.all_permissions && Array.isArray(userData.all_permissions) && userData.all_permissions.length > 0) {
          const permissionNames = userData.all_permissions.map(p => typeof p === 'string' ? p : p.name);
          // console.log('🔐 RBAC DEBUG - Using all_permissions from getUserDetails (matches web getAllPermissions):', permissionNames);
          // console.log('🔐 RBAC DEBUG - Total permissions:', permissionNames.length);
          setPermissions(permissionNames);

          // Set user role from userData for display
          if (userData.roles && userData.roles.length > 0) {
            setUserRole(userData.roles[0]);
          }
        }
        // FALLBACK: If all_permissions is not available (e.g. for Admin users API omits it),
        // fetch from getUserRoles API
        else {
          // console.log('🔐 RBAC DEBUG - all_permissions not available, fetching from getUserRoles API...');
          try {
            const userRolesResponse = await apiService.getUserRoles(userData.business_id);
            const roles = userRolesResponse.data || userRolesResponse;

            if (roles && roles.length > 0) {
              const allPermissionNames = [];
              roles.forEach(role => {
                if (role.permissions) {
                  role.permissions.forEach(p => {
                    if (p.name && !allPermissionNames.includes(p.name)) {
                      allPermissionNames.push(p.name);
                    }
                  });
                }
              });
              // console.log('🔐 RBAC DEBUG - Permissions from getUserRoles:', allPermissionNames);
              setUserRole(roles[0]);
              setPermissions(allPermissionNames);
            } else {
              // console.log('🔐 RBAC DEBUG - No roles found from API');
              setPermissions([]);
            }
          } catch (roleError) {
            // console.error('🔐 RBAC DEBUG - Error fetching user roles:', roleError);
            setPermissions([]);
          }
        }
      } else {
        // console.log('🔐 RBAC DEBUG - No user details found');
      }
    } catch (error) {
      // console.error('🔐 RBAC DEBUG - Error loading user permissions:', error);
      // console.log('🔐 RBAC DEBUG - Error details:', error.message, error.stack);
    } finally {
      setIsLoading(false);
      // console.log('🔐 RBAC DEBUG - Permission loading completed');
      // console.log('🔐 RBAC DEBUG - Final permissions state will be updated asynchronously');
      // Log current state after a small delay to see final values
      setTimeout(() => {
        // console.log('🔐 RBAC DEBUG - Final permissions count:', permissions.length);
        // console.log('🔐 RBAC DEBUG - Final permissions:', permissions);
      }, 100);
    }
  };

  const checkUserDataRoles = async (userData) => {
    // console.log('🔐 RBAC DEBUG - Checking user data roles...');

    // Check if user has all_permissions field (direct permissions)
    if (userData.all_permissions && Array.isArray(userData.all_permissions)) {
      // console.log('🔐 RBAC DEBUG - Found all_permissions field:', userData.all_permissions);
      // console.log('🔐 RBAC DEBUG - Permission count:', userData.all_permissions.length);

      // Set permissions directly from all_permissions
      setPermissions(userData.all_permissions);

      // Create a mock role object for consistency
      const mockRole = {
        id: 'direct-permissions',
        name: 'Direct Permissions',
        permissions: userData.all_permissions.map(p => ({ name: p }))
      };
      setUserRole(mockRole);

      // console.log('🔐 RBAC DEBUG - Permissions set from all_permissions:', userData.all_permissions);
      return;
    }

    // Fallback to checking user data directly for roles
    if (userData.roles && userData.roles.length > 0) {
      const role = userData.roles[0];
      // console.log('🔐 RBAC DEBUG - User role from user data:', role);
      setUserRole(role);

      if (role.permissions) {
        const permissionNames = role.permissions.map(p => p.name);
        // console.log('🔐 RBAC DEBUG - User permissions from user data:', permissionNames);
        setPermissions(permissionNames);
      } else {
        // console.log('🔐 RBAC DEBUG - No permissions found in role from user data');
      }
    } else {
      // console.log('🔐 RBAC DEBUG - No roles or all_permissions found in user data');
      // console.log('🔐 RBAC DEBUG - User data structure:', Object.keys(userData));
      // console.log('🔐 RBAC DEBUG - Setting empty permissions as fallback');
      setPermissions([]);
      setUserRole(null);
    }
  };

  const superadminAbilities = ['superadmin', 'superadmin.access', 'manage_modules', 'backup'];

  const hasPermission = (permission) => {
    if (!permission) return true;
    if (isLoading) return false;

    // Check for superadmin access - only true superadmins bypass superadmin checks
    if (isSuperAdmin) return true;

    // Match web Gate::before logic: Admin role users bypass non-superadmin permission checks
    const isSuperadminAbility = superadminAbilities.includes(permission);
    if (!isSuperadminAbility) {
      const isAdminRole = userRole && (userRole.name?.startsWith('Admin') || userRole.name?.toLowerCase() === 'administrator');
      if (isAdminRole) return true;
    }

    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    if (!permissionList || permissionList.length === 0) return true;
    if (isLoading) return false;
    if (isSuperAdmin) return true;

    // Match web Gate::before: Admin role bypasses non-superadmin abilities
    const hasOnlySuperadminAbilities = permissionList.every(p => superadminAbilities.includes(p));
    if (!hasOnlySuperadminAbilities) {
      const isAdminRole = userRole && (userRole.name?.startsWith('Admin') || userRole.name?.toLowerCase() === 'administrator');
      if (isAdminRole) return true;
    }

    return permissionList.some(permission => permissions.includes(permission));
  };

  const hasAllPermissions = (permissionList) => {
    if (!permissionList || permissionList.length === 0) return true;
    if (isLoading) return false;
    if (isSuperAdmin) return true;

    // Match web Gate::before: Admin role bypasses non-superadmin abilities
    const hasAnySuperadminAbility = permissionList.some(p => superadminAbilities.includes(p));
    if (!hasAnySuperadminAbility) {
      const isAdminRole = userRole && (userRole.name?.startsWith('Admin') || userRole.name?.toLowerCase() === 'administrator');
      if (isAdminRole) return true;
    }

    return permissionList.every(permission => permissions.includes(permission));
  };

  const value = {
    permissions,
    userRole,
    businessId,
    isSuperAdmin,
    isLoading,
    enabledModules,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions: loadUserPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
